<template>
  <v-container fluid>
    <v-card outlined elevation="1">

      <!-- HEADER -->
      <v-toolbar color="primary" dark dense flat>
        <v-toolbar-title>Completion Report</v-toolbar-title>

        <v-spacer />

        <v-btn small text color="white" @click="printReport(reportId)">
          <v-icon left>mdi-printer</v-icon>
          Print
        </v-btn>

        <v-btn small text color="white" @click="duplicate">
          <v-icon left>mdi-content-duplicate</v-icon>
          Duplicate
        </v-btn>

        <v-btn small text color="white" :to="'/completion-reports'">
          <v-icon left>mdi-arrow-left</v-icon>
          Back
        </v-btn>
      </v-toolbar>

      <!-- CONTENT -->
      <v-card-text>
        <v-row dense>

          <!-- META -->
          <v-col cols="12" md="4">
            <strong>Reference No</strong>
            <div class="mt-1">{{ inputs.ref_no || '-' }}</div>
          </v-col>

          <v-col cols="12" md="4">
            <strong>Date</strong>
            <div class="mt-1">{{ inputs.date || '-' }}</div>
          </v-col>

          <v-col cols="12" md="4">
            <strong>Customer ID</strong>
            <div class="mt-1">{{ inputs.customer_id || '-' }}</div>
          </v-col>

          <!-- PROJECT -->
          <v-col cols="12">
            <strong>Project Name</strong>
            <div class="mt-1">{{ inputs.project_name || '-' }}</div>
          </v-col>

          <v-col cols="12">
            <strong>Description</strong>
            <div class="mt-1 pre-wrap">{{ inputs.description || '-' }}</div>
          </v-col>

          <!-- SCOPE -->
          <v-col cols="12">
            <strong>Scope of Works</strong>
            <v-list dense class="mt-2">
              <v-list-item v-for="(item, index) in inputs.scope_of_works" :key="'scope-' + index">
                <v-list-item-content>
                  <v-list-item-title>{{ index + 1 }}. {{ item }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="!inputs.scope_of_works.length">
                <v-list-item-content>
                  <v-list-item-title class="grey--text">No scope of works provided</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-col>

          <!-- WORK COMPLETED -->
          <v-col cols="12">
            <strong>Work Completed</strong>
            <div class="mt-1 pre-wrap">{{ inputs.work_completed.join('\n') || '-' }}</div>
          </v-col>

          <!-- CONCLUSION -->
          <v-col cols="12">
            <strong>Conclusion</strong>
            <div class="mt-1 pre-wrap">{{ inputs.conclusion || '-' }}</div>
          </v-col>

          <!-- IMAGES -->
          <v-col cols="12">
            <strong>Images</strong>

            <!-- Auto rows based on span -->
            <v-row dense v-for="(row, rowIndex) in imageRows" :key="'row-' + rowIndex" align="start">
              <v-col v-for="(img, i) in row" :key="'img-' + rowIndex + '-' + i"
                :cols="Math.floor(spanToNumber(img.span) * 12)">
                <v-img v-if="img.preview" :src="img.preview" max-height="200" contain class="mt-2 grey lighten-4" />
                <div v-else class="grey--text mt-2">No image provided</div>
                <div class="text-caption mt-1">{{ img.name || 'No name' }}</div>
              </v-col>
            </v-row>
          </v-col>

        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import appAxios from '@/libs/app-axios'
import { LoadingPopup, ErrorPopup } from '@/libs/utils'

export default {
  name: 'ShowCompletionReport',
  data() {
    return {
      inputs: {
        ref_no: '',
        project_name: '',
        description: '',
        date: '',
        customer_id: '',
        scope_of_works: [],
        work_completed: [],
        conclusion: '',
        images: [],
      },
    }
  },
  computed: {
    reportId() {
      return this.$route.params.completionReportId
    },
    imageRows() {
      const rows = []
      let currentRow = []
      let currentSpan = 0

      this.inputs.images.forEach(img => {
        const span = this.spanToNumber(img.span || '1/2')
        if (currentSpan + span > 1) {
          rows.push(currentRow)
          currentRow = []
          currentSpan = 0
        }
        currentRow.push(img)
        currentSpan += span
      })

      if (currentRow.length) rows.push(currentRow)
      return rows
    },
  },
  mounted() {
    this.fetchReport()
  },
  methods: {
    spanToNumber(span) {
      if (span === '1') return 1
      if (span === '1/2') return 0.5
      if (span === '1/3') return 1 / 3
      return 0.5
    },
    async fetchReport() {
      try {
        LoadingPopup.fire('Loading...')
        const res = await appAxios().get(`/v3/completion-reports/${this.reportId}`)
        const data = res.data

        this.inputs = { ...this.inputs, ...data }

        this.inputs.scope_of_works = Array.isArray(data.scope_of_works)
          ? data.scope_of_works
          : (data.scope_of_works ?? '').split('\n').filter(i => i.trim() !== '')

        this.inputs.work_completed = Array.isArray(data.work_completed)
          ? data.work_completed
          : (data.work_completed ?? '').split('\n').filter(i => i.trim() !== '')

        this.inputs.images = (data.images || []).map(img => ({
          id: img.id,
          name: img.name,
          span: img.span || '1/2',
          preview: img.preview,
        }))

        LoadingPopup.close()
      } catch (e) {
        LoadingPopup.close()
        console.error(e)
        ErrorPopup.fire('Failed to load report')
      }
    },

    async printReport(id) {
      try {
        const { data } = await appAxios().get(`/v3/completion-reports/${id}/pdf`, { responseType: 'blob' })
        const fileURL = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
        window.open(fileURL, '_blank')
      } catch (e) {
        ErrorPopup.fire('Failed to generate PDF')
      }
    },

    duplicate() {
      this.$router.push({
        name: 'CompletionReports.Create',
        query: { duplicateFrom: this.reportId },
      })
    },
  },
}
</script>

<style scoped>
.pre-wrap {
  white-space: pre-wrap;
}
</style>